import { type FetchEventsResult, CalendarApiResponseSchema } from '@classic/Components/EventsSlider/Schemas';
import { InvalidSchemaError } from '@classic/Components/EventsSlider/Errors/InvalidSchema';
import { RequestError } from '@classic/Components/EventsSlider/Errors/RequestError';
import { type ViewSource } from '@classic/Components/EventsSlider/Types';

class EventsRepository
{
    /**
     * Fetches events from the calendar API
     *
     * @param calendarId - The calendar ID to fetch events for
     * @param fromDate - The start date to fetch events for (YYYY-MM-DD format)
     * @param toDate - The end date to fetch events for (YYYY-MM-DD format)
     * @param viewSource - The source of the view (event-slider or full-calendar)
     * @returns FetchEventsResult containing events and site timezone
     */
    public async fetchEvents(
        calendarId: number,
        fromDate: string,
        toDate: string,
        viewSource: ViewSource = 'event-slider',
    ): Promise<FetchEventsResult> {
        const url = new URL(`/api/calendars/${calendarId}/events`, window.location.origin);
        url.searchParams.set('start_date', fromDate);
        url.searchParams.set('end_date', toDate);
        url.searchParams.set('view_source', viewSource);

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();
        const validationResult = CalendarApiResponseSchema.safeParse(jsonData);

        if (!validationResult.success) {
            console.error('Invalid API response:', validationResult.error, validationResult);
            throw new InvalidSchemaError('Invalid API response', validationResult.error);
        }

        const apiResponse = validationResult.data;

        if (!apiResponse.success || apiResponse.data.error) {
            console.error('API returned error:', apiResponse.data.error);
            throw new RequestError(apiResponse.data.error);
        }

        return {
            events: apiResponse.data.events,
            siteTimezone: apiResponse.data.site_timezone,
        };
    }
}

export default EventsRepository;
